import React, { useState } from 'react'
import { useMutation } from 'react-query'
import { HelpOutline } from '@mui/icons-material'
import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useIntl } from 'react-intl'

import { recognitionIcons } from 'constants/recognition'
import { t } from 'i18n'
import { API } from 'core/requests'

export default function ManageRecognitionInfo({ formik, recognitionsEventGroups }) {
  const intl = useIntl()

  const [eventGroup, setEventGroup] = useState<any>(recognitionsEventGroups || []);
  const [editGroup, setEditGroup] = useState<any>(null);
  const [deleteGroup, setDeleteGroup] = useState<any>(null);

  const openEditDialog = (group: any) => {
    setEditGroup(group);
  };

  const openDeleteDialog = (group: any) => {
    setDeleteGroup(group);
  };

  const updateEventGroup = useMutation(
    (payload: any) => {
      return API.update('recognitionsEventGroup', payload)
    },
    {
      onSettled: () => {
        setEditGroup(null)
      },
    },
  )

  const deleteEventGroup = useMutation(
    (payload: any) => API.delete('recognitionsEventGroup', payload),
    {
      onSettled: () => {
        setDeleteGroup(null)
      },
    },
  )

  const handleSaveGroup = (editGroup: any) => {
    setEventGroup((prev: any) =>
      prev.map((group: any) =>
        group.id === editGroup.id
          ? { ...group, name: editGroup.name }
          : group
      )
    )

    updateEventGroup.mutate(editGroup)
  }

  const handleDeleteGroup = (deleteGroupId: number) => {
    setEventGroup((prev: any) =>
      prev.filter((group: any) => group.id !== deleteGroupId)
    );
    deleteEventGroup.mutate(deleteGroupId)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          data-cy='name'
          id='name'
          name='name'
          label={t('name')}
          placeholder={intl.formatMessage({
            id: 'recognitions.fields.wayToGoTeam',
          })}
          variant='outlined'
          value={formik.values.name}
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          data-cy='programIcon'
          select
          id='icon'
          name='icon'
          label={t('recognitions.fields.programIcon')}
          placeholder={intl.formatMessage({
            id: 'recognitions.fields.programIconPlaceholder',
          })}
          variant='outlined'
          value={formik.values.icon}
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.icon && Boolean(formik.errors.icon)}
          helperText={formik.touched.icon && formik.errors.icon}
          fullWidth
        >
          {recognitionIcons.map(recognitionIcon => (
            <MenuItem
              data-cy='icon_option'
              key={recognitionIcon.value}
              value={recognitionIcon.value}
            >
              <Grid container alignItems='center'>
                <recognitionIcon.Icon
                  style={{ marginRight: 8, height: 14, width: 14 }}
                />
                {recognitionIcon.label}
              </Grid>
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <TextField
          data-cy='description'
          id='description'
          name='description'
          label={t('description')}
          placeholder={intl.formatMessage({
            id: 'recognitions.fields.extraMile',
          })}
          variant='outlined'
          value={formik.values.description ?? ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          multiline
          rows={3}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={formik.touched.description && formik.errors.description}
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          data-cy='event-group-id'
          select
          id='group_id'
          name='group_id'
          label={t('recognitions.fields.eventGroup')}
          placeholder={t('recognitions.fields.eventGroupPlaceholder')}
          variant='outlined'
          value={formik.values.group_id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.group_id && Boolean(formik.errors.group_id)}
          helperText={formik.touched.group_id && formik.errors.group_id}
          fullWidth
        >
          <MenuItem
            data-cy='event-group-option'
            key={`no-event-group`}
            value={''}
          >
            <Grid container alignItems='center'>
              {t('recognitions.fields.noEventGroup')}
            </Grid>
          </MenuItem>
          <MenuItem
            data-cy='event-group-option'
            key={`add-new-event-group`}
            value={0}
          >
            <Grid container alignItems='center'>
              {t('recognitions.fields.addNewEventGroup')}
            </Grid>
          </MenuItem>
          {eventGroup.map((group: any) => {
            const isSelected = formik.values.group_id === group.id;

            return (
              <MenuItem
                data-cy="event-group-option"
                key={group.id}
                value={group.id}
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <span>{group.name}</span>

                  {!isSelected && (
                    <Box display="flex" alignItems="center">
                      <EditIcon
                        color="primary"
                        style={{ marginLeft: 8, height: 14, width: 14, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(group);
                        }}
                      />
                      <DeleteIcon
                        color="error"
                        style={{ marginLeft: 8, height: 14, width: 14, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(group);
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </MenuItem>
            );
          })}
        </TextField>
      </Grid>

      {
        formik.values.group_id === 0 && (
          <Grid item xs={12}>
            <TextField
              data-cy='event-group-name'
              id='group_name'
              name='group_name'
              label={t('Group Name')}
              placeholder={intl.formatMessage({
                id: 'recognitions.fields.eventGroupNamePlaceholder',
              })}
              variant='outlined'
              value={formik.values.group_name}
              required
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.group_name && Boolean(formik.errors.group_name)}
              helperText={formik.touched.group_name && formik.errors.group_name}
              fullWidth
            />
          </Grid>
        )
      }

      {formik.values.group_id && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formik.values.is_default}
                onChange={() =>
                  formik.setFieldValue('is_default', !formik.values.is_default)
                }
                name='is_default'
                color='primary'
              />
            }
            label={t('recognitions.fields.isDefault')}
          />
        </Grid>
      )}

      {formik.values.icon === 'anniversary' && (
        <Grid item xs={12}>
          <TextField
            data-cy='event-milestone-value'
            id='milestone_value'
            name='milestone_value'
            label={t('recognitions.fields.eventMilestoneValue')}
            placeholder={intl.formatMessage({
              id: 'recognitions.fields.eventMilestoneValuePlaceholder',
            })}
            type='number'
            variant='outlined'
            value={formik.values.milestone_value ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.milestone_value && Boolean(formik.errors.milestone_value)}
            helperText={formik.touched.milestone_value && formik.errors.milestone_value}
            fullWidth
            inputProps={{ min: 1 }}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Grid container>
          <FormControlLabel
            style={{
              marginRight: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
            control={
              <Checkbox
                checked={!!formik.values.has_group}
                onChange={() =>
                  formik.setFieldValue('has_group', !formik.values.has_group)
                }
                name='has_group'
                color='primary'
                data-cy='checkbox'
              />
            }
            label={t('recognitions.fields.allowGroupRecognition')}
          />
        </Grid>
      </Grid>

      {formik.values.has_group && (
        <Grid item xs={12}>
          <Typography variant='body1' color='text.secondary'>
            <Grid component='span' container alignItems='center'>
              {t('recognitions.groupRecognitionsBudgetImpact')}
              <Tooltip
                title={t('recognitions.groupRecognitionsBudgetImpactTooltip')}
              >
                <HelpOutline fontSize='small' style={{ marginLeft: 8 }} />
              </Tooltip>
            </Grid>
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              data-cy='hasNomination'
              id='has_nominations'
              name='has_nominations'
              checked={formik.values.has_nominations}
              onChange={() =>
                formik.setFieldValue(
                  'has_nominations',
                  !formik.values.has_nominations,
                )
              }
            />
          }
          label={'Make this an Employee Nomination Event too'}
        />
      </Grid>

      {/* SFBay Phase II: hidden on General tab to avoid confusion with period/limit settings on Advanced tab. */}
      {/* <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          justifyContent='space-between'
          alignItems='center'
        >
          <Grid item xs={12} sm='auto'>
            <FormControlLabel
              control={
                <Checkbox
                  data-cy='has_maximum_number_of_recognitions'
                  name='has_maximum_number_of_recognitions'
                  checked={!!formik.values.has_maximum_number_of_recognitions}
                  onChange={() =>
                    formik.setFieldValue(
                      'has_maximum_number_of_recognitions',
                      !formik.values.has_maximum_number_of_recognitions,
                    )
                  }
                />
              }
              label='Limit the number of recognitions that is possible to send'
            />
          </Grid>

          {formik.values.has_maximum_number_of_recognitions && (
            <Grid item xs={12} sm>
              <TextField
                data-cy='maximum_recognitions_number'
                id='maximum_number_of_recognitions'
                name='maximum_number_of_recognitions'
                label='Recognitions limit for this event'
                type='number'
                variant='outlined'
                value={formik.values.maximum_number_of_recognitions}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.maximum_number_of_recognitions &&
                  Boolean(formik.errors.maximum_number_of_recognitions)
                }
                helperText={
                  formik.touched.maximum_number_of_recognitions &&
                  formik.errors.maximum_number_of_recognitions
                }
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </Grid> */}

      <Dialog open={!!editGroup} onClose={() => setEditGroup(null)} fullWidth>
        <DialogTitle>Edit Event Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group Name"
            defaultValue={editGroup?.name}
            onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGroup(null)}>Cancel</Button>
          <Button onClick={() => handleSaveGroup(editGroup)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteGroup} onClose={() => setDeleteGroup(null)}>
        <DialogTitle>Delete Event Group</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteGroup?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGroup(null)}>Cancel</Button>
          <Button color="error" onClick={() => handleDeleteGroup(deleteGroup.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}
